<template>
  <div class="warning-panel">
    <div class="warning-toolbar">
      <div class="warning-form-group">
        <label class="warning-label">选择月份</label>
        <el-select
          v-model="selectedMonths"
          multiple
          collapse-tags
          placeholder="请选择月份"
          style="width: 320px"
        >
          <el-option
            v-for="m in monthOptions"
            :key="m.value"
            :label="m.label"
            :value="m.value"
          />
        </el-select>
      </div>
      <div class="warning-form-group">
        <label class="warning-label">预警阈值（元）</label>
        <el-input-number
          v-model="threshold"
          :min="0"
          :precision="2"
          :step="1000"
        />
      </div>
      <el-button type="primary" :loading="warningLoading" @click="queryWarning"
        >查询预警</el-button
      >
    </div>

    <div class="warning-section">
      <div class="warning-section-header">
        <h3>实际承运人月度打款预警</h3>
        <el-badge
          :value="totalDriverCount"
          :max="99999"
          class="warning-badge"
        />
      </div>
      <div class="warning-desc">
        统计所选月份内，实际承运人收款总金额超过
        <strong>{{ threshold }}</strong> 元的实际承运人（测试环境阈值）。
      </div>

      <div v-loading="warningLoading" class="warning-body">
        <!-- 按月分组展示：标题含该月统计的时间区间（与原型一致） -->
        <template v-if="warningGroups.length">
          <div
            v-for="g in warningGroups"
            :key="g.month"
            class="warning-month-block"
          >
            <div class="warning-month-title">
              {{ g.monthLabel }}（{{ g.startTime }} 至 {{ g.endTime }}）
            </div>
            <el-table
              v-if="g.drivers.length"
              :data="g.drivers"
              border
              stripe
              size="small"
              style="width: 100%"
            >
              <el-table-column type="index" label="序号" width="60" />
              <el-table-column
                prop="driverName"
                label="实际承运人姓名"
                min-width="120"
              />
              <el-table-column
                prop="driverPhone"
                label="实际承运人电话"
                min-width="140"
              />
              <el-table-column
                prop="account"
                label="收款账号"
                min-width="160"
                show-overflow-tooltip
              />
              <el-table-column
                label="月度打款总金额"
                min-width="150"
                align="right"
              >
                <template slot-scope="scope">
                  <span class="amount-cell"
                    >¥ {{ formatAmount(scope.row.totalAmount) }}</span
                  >
                </template>
              </el-table-column>
            </el-table>
            <div v-else class="warning-empty">
              该月份暂无超过阈值的实际承运人
            </div>
          </div>
        </template>
        <el-empty v-else-if="!warningLoading" description="暂无预警数据" />
      </div>
    </div>
  </div>
</template>

<script>
import { driverRemitWarning } from "@/api/smartAssistant";

export default {
  name: "WarningPanel",
  data() {
    // 最近 12 个月作为可选项
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      months.push({ value: `${y}-${m}`, label: `${y}年${m}月` });
    }
    return {
      selectedMonths: [months[0].value],
      monthOptions: months,
      threshold: 10,
      warningLoading: false,
      warningGroups: [],
      totalDriverCount: 0
    };
  },
  methods: {
    queryWarning() {
      if (!this.selectedMonths.length) {
        this.$message.warning("请至少选择一个月份");
        return;
      }
      this.warningLoading = true;
      driverRemitWarning({
        months: this.selectedMonths.join(","),
        threshold: this.threshold
      })
        .then(res => {
          const body = res.data || {};
          const monthList = body.data || [];
          const groups = [];
          let total = 0;
          monthList.forEach(m => {
            const drivers = (m.drivers || []).map(d => ({
              driverName: d.driverName || "-",
              driverPhone: d.driverPhone || "-",
              account: d.account || "-",
              totalAmount: d.totalAmount || 0
            }));
            total += drivers.length;
            groups.push({
              month: m.month,
              monthLabel: m.month_label,
              startTime: m.start_time,
              endTime: m.end_time,
              drivers
            });
          });
          this.warningGroups = groups;
          this.totalDriverCount = total;
          if (!monthList.length) {
            this.$message.info("暂无符合条件的实际承运人");
          }
        })
        .catch(() => {
          // 错误提示已由 request 拦截器统一处理
        })
        .finally(() => {
          this.warningLoading = false;
        });
    },
    formatAmount(v) {
      const n = Number(v);
      return isNaN(n) ? "0.00" : n.toFixed(2);
    }
  }
};
</script>

<style lang="scss" scoped>
.warning-panel {
  .warning-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 20px;
  }
  .warning-form-group {
    display: flex;
    align-items: center;
  }
  .warning-label {
    margin-right: 8px;
    color: #666;
    white-space: nowrap;
  }
  .warning-section-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    h3 {
      margin: 0;
      color: #333;
    }
  }
  .warning-desc {
    font-size: 12px;
    color: #666;
    margin-bottom: 12px;
  }
  .warning-body {
    min-height: 60px;
  }
  .warning-month-block {
    margin-bottom: 16px;
  }
  .warning-month-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 16px 0 10px;
    padding-left: 8px;
    border-left: 3px solid #ff4d4f;
    &:first-child {
      margin-top: 0;
    }
  }
  .warning-empty {
    color: #999;
    padding: 12px;
    font-size: 13px;
    background: #fafafa;
    border-radius: 4px;
  }
  .amount-cell {
    color: #cf1322;
    font-weight: 600;
  }
}
</style>
